import React from 'react';
import { TextField, Button, Box, Typography, Stepper, Step, StepLabel } from '@mui/material';
import { auth, db } from '../firebase';
import { updateDoc, doc, addDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth'; 
import { useNavigate } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { setShopName } from '../redux/shopSlice';

import logo from '../images/logo.svg';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// 店舗情報設定画面
// ・店舗名やポイントを設定する
// ・Stripe のチェックアウトを発行することで、Run Payments with Stripeを呼び出し、stripeのカスタマーを生成する
// ・上記の拡張機能の関数でstripeのカスタマーが作成されると、その情報がFirestoreの customer ドキュメントに書き込まれるので、そこにトライアル終了日時も一緒に書き込む
const ShopNew = () => {
  const [user] = useAuthState(auth);
  const shopName = useSelector((state) => state.shop.shopName);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleNameChange = (event) => {
    dispatch(setShopName(event.target.value));
  };

  const handleSave = async () => {
    // 入力チェック
    if (!shopName) {
      alert('店舗名を入力してください。');
      return;
    }
    try {
      // 現在のユーザーに対応する customer ドキュメント参照
      const userRef = doc(db, 'customers', user.uid);

      // トライアル終了日時を計算して Firestore に書き込む
      const trialDays = parseInt(process.env.REACT_APP_TRIAL_PERIOD_DAYS || '30');
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + trialDays);
      
      await updateDoc(userRef, {
        shop_id: user.uid,
        shop_name: shopName,
        point_rate: 100, // デフォルト値を固定。後から ShopInfo で変更可能
        img_url: null,
        // App.js で画面切り替え判定に利用する
        trial_end: trialEndDate,
      });

      // === Stripe チェックアウトセッション生成 ===
      // この画面ではリダイレクトを行わない。セッションを書き込めば
      // 拡張機能が勝手に Stripe カスタマーとサブスクリプションを作成する。
      // 元々はリダイレクトしていたが、決済情報登録は任意にしたいため、URLは発行するがリダイレクトはしない運用に変更。
      // ただし、今後、リダイレクト運用に戻す可能性もあるので、セッション生成のコードは残しておく。
      // なお trial_period_days は上で保存した trial_end と同じ期間。
      const checkoutSession = {
        automatic_tax: true,
        cancel_url: `${window.location.origin}/menu`,
        collect_shipping_address: true,
        line_items: [
          {
            price: process.env.REACT_APP_STRIPE_PRICE_ID,
            quantity: 1,
          }
        ],
        metadata: {
          key: 'value',
        },
        mode: 'subscription',
        trial_period_days: parseInt(process.env.REACT_APP_TRIAL_PERIOD_DAYS || '30'),
        success_url: window.location.origin,
        tax_id_collection: true,
        subscription_data: {
          items: [
            {
              price: process.env.REACT_APP_STRIPE_PRICE_ID
            }
          ],
        },
      };

      // セッションリクエストを書き込み。拡張機能がこの変更を検知し
      // Stripe API を呼び出してくれる。
      const docRef = await addDoc(collection(db, 'customers', user.uid, 'checkout_sessions'), checkoutSession);
      
      // ナビゲーション前に少し待って Firestore の同期を完了させる
      const checkoutPromise = new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data();
          if (data && data.error) {
            console.error(`Checkout session error: ${data.error.message}`);
            unsubscribe();
            reject(new Error(data.error.message));
          }
          // ドキュメントが作成されたら終了。URL は不要（拡張機能が対応）。
        });
        
        setTimeout(() => {
          unsubscribe();
          resolve();
        }, 500);
      });
      
      await checkoutPromise;
      // Firestore 書き込みが両方終わったらトップ画面へ
      navigate('/');
      
    } catch (error) {
      console.error('エラー', error);
    }
    //必要に応じて、フォームを初期化するなどの処理を追加
  };

  const steps = [
    <>
      <Typography align="center">アカウント<br />作成</Typography>
    </>,
    <>
      <Typography align="center">店舗名<br />設定</Typography>
    </>,
    <>
      <Typography align="center">無料トライアル<br />開始</Typography>
    </>,
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', px: 0, py: 0 }}>
      <Box sx={{ backgroundColor:'#f9db00', textAlign: 'center', mb: 2, py: 2 }}>
        <img src={logo} alt="Conekuta" style={{ height: '1.25rem' }} />
      </Box>
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={1} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center', px: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3, fontSize: '0.9rem' }}>
          ※設定は後から変更できます
        </Typography>
        <TextField
          label="店舗名"
          fullWidth
          variant="outlined"
          margin="normal"
          required
          value={shopName}
          onChange={handleNameChange}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ mt: 2, width: '100%', fontWeight: 'bold' }}
          endIcon={<ArrowForwardIosIcon fontSize="small" />}
        >
          確定
        </Button>
      </Box>
    </Box>
  );
};

export default ShopNew;
